import { Apple, CircleCheck, Flag, Leaf, Lightbulb, Plane, Smile, Volleyball } from "lucide-react";

// Type definitions for emoji-mart
export interface EmojiMartEmoji {
  id: string;
  name: string;
  native: string;
  keywords?: string[];
  skins?: Array<{ native: string }>;
  search: string;
}

export interface EmojiMartCategory {
  id: string;
  name: string;
  emojis: string[];
}

export interface EmojiMartData {
  categories: EmojiMartCategory[];
  emojis: Record<string, EmojiMartEmoji>;
}

let emojiMartData: EmojiMartData | null = null;

// Load emoji-mart data lazily
export const getEmojiMartData = async (): Promise<EmojiMartData> => {
  if (emojiMartData) return emojiMartData;
  
  try {
    const data = await import('@emoji-mart/data');
    emojiMartData = data.default as EmojiMartData;
    return emojiMartData;
  } catch (error) {
    console.error('Failed to load emoji-mart data:', error);
    throw error;
  }
};

// Search emojis using emoji-mart data
export const searchEmojis = async (query: string): Promise<EmojiMartEmoji[]> => {
  if (!query.trim()) return [];
  
  const data = await getEmojiMartData();
  const searchTerm = query.toLowerCase();
  
  return Object.values(data.emojis).filter(emoji => {
    // Search in emoji name
    if (emoji.name.toLowerCase().includes(searchTerm)) return true;
    
    // Search in keywords
    if (emoji.keywords?.some(keyword => keyword.toLowerCase().includes(searchTerm))) return true;
    
    // Search in search field (contains various terms)
    if (emoji.search?.toLowerCase().includes(searchTerm)) return true;
    
    return false;
  }).slice(0, 50); // Limit results for performance
};

// Get emojis by category using emoji-mart data
export const getEmojisByCategory = async (categoryId: string): Promise<EmojiMartEmoji[]> => {
  const data = await getEmojiMartData();
  const category = data.categories.find(cat => cat.id === categoryId);
  
  if (!category) return [];
  
  return category.emojis.map(emojiId => data.emojis[emojiId]).filter(Boolean);
};

// Get emoji categories from emoji-mart data
export const getEmojiCategories = async () => {
  const data = await getEmojiMartData();
  return data.categories;
};

// Category icons mapping for UI - using React components for consistency
export const getCategoryIcon = (categoryId: string) => {
  const iconMap: Record<string, React.ReactElement> = {
    'people': <Smile size={20} />,
    'nature': <Leaf size={20} />,
    'foods': <Apple size={20} />,
    'activity': <Volleyball size={20} />,
    'places': <Plane size={20} />,
    'objects': <Lightbulb size={20} />,
    'symbols': <CircleCheck size={20} />,
    'flags': <Flag size={20} />
  };
  
  // Fallback for any category not in our mapping
  return iconMap[categoryId] || <Smile size={20} />;
};

// Helper function to get the emoji character from emoji data
export const getEmojiCharacter = (emoji: EmojiMartEmoji): string => {
  // Try native first
  if (emoji.native) return emoji.native;
  
  // Try first skin variation
  if (emoji.skins?.[0]?.native) return emoji.skins[0].native;
  
  // If the ID looks like a Unicode sequence (contains 'u' or numbers/letters)
  if (emoji.id && emoji.id.includes('-')) {
    try {
      // Convert Unicode codepoints to emoji
      const codepoints = emoji.id.split('-').map(code => {
        // Handle both hex (like "1f600") and decimal formats
        const num = code.startsWith('u') ? parseInt(code.slice(1), 16) : parseInt(code, 16);
        return String.fromCodePoint(num);
      }).join('');
      return codepoints;
    } catch (e) {
      console.warn('Failed to convert emoji ID to character:', emoji.id, e);
    }
  }
  
  // Fallback to ID (might be a text representation)
  return emoji.id;
};